import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import { commonDataGridStyles } from '@rossum/components/data-grid/styles';
import { Filters } from '@rossum/components/filtering';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import {
  Dialog,
  LinearProgress,
  Link,
  selectClasses,
  Stack,
} from '@rossum/ui/material';
import {
  DataGridPro,
  GridPaginationModel,
  GridRow,
  GridRowParams,
  GridRowProps,
} from '@rossum/ui/x-data-grid-pro';
import { usePatchUser } from '../../../business/users/usePatchUser';
import { useUsers } from '../../../business/users/useUsers';
import { pageSizeOptions } from '../../../constants/values';
import {
  getRoleName,
  isUserAdminLike,
} from '../../../redux/modules/groups/helpers';
import { groupsSelector } from '../../../redux/modules/groups/selectors';
import { USERS_LIST_QUERY } from '../../../redux/modules/localStorage/actions';
import { OrganizationUser } from '../../../types/user';
import { useStoredQuery } from '../../../utils/hooks/useStoredQuery';
import { useWorkspacesWithQueues } from '../../queues/hooks/useWorkspacesWithQueues';
import { useUser } from '../../user-panel/hooks/useUser';
import { DeleteUserDialog } from '../components/delete-user/DeleteUserDialog';
import { useUsersColumns } from '../components/hooks/useUsersColumns';
import { UsersHeader } from '../components/UsersHeader';
import { getQueueAndWorkspacesCounts } from '../helpers';
import { useExportUsers } from '../hooks/useExportUsers';
import {
  usersQueryParser,
  useUsersFilterSettings,
} from '../hooks/useUsersFilterSettings';
import { userDetailsAccessPath } from '../routes';

const UserRow = (props: GridRowProps) => (
  <Link
    component={RouterLink}
    to={userDetailsAccessPath(props.row['id'])}
    onClick={e => {
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
    }}
    sx={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
  >
    <GridRow {...props} />
  </Link>
);

export const Users = () => {
  const intl = useIntl();
  const { push } = useHistory();

  const [parsedParams, updateParsedParams] = useStoredQuery({
    parser: usersQueryParser,
    localStorageKey: USERS_LIST_QUERY,
  });

  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(
    null
  );

  const groups = useSelector(groupsSelector);

  const { data: loggedInUser } = useUser();

  const loggedInUserId = loggedInUser?.id;

  const {
    queues,
    workspaces,
    isLoading: isQueuesLoading,
  } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const { data: dataActiveUsers } = useUsers({
    isActive: true,
    pageSize: 1,
    page: 1,
  });

  const { mutate: patchUser } = usePatchUser();

  const { mutate: exportUsers, isLoading: isExporting } = useExportUsers();

  const {
    pagination: { total: activeUsersCount },
  } = dataActiveUsers ?? { pagination: { total: 0 } };

  const queueId = queues?.find(q => q.id === parsedParams.queue)?.id;

  const {
    data: users,
    isLoading,
    isPreviousData,
  } = useUsers({
    deleted: false,
    pageSize: Number(parsedParams.pageSize),
    page: Number(parsedParams.page),
    search: parsedParams.search,
    groups: parsedParams.role ? [parsedParams.role] : undefined,
    isActive:
      parsedParams.isActive !== undefined
        ? parsedParams.isActive === 'true'
        : undefined,
    queues: queueId ? [queueId] : undefined,
  });

  const filterSettings = useUsersFilterSettings({
    groups,
    queues,
    workspaces,
    isQueuesLoading,
    setFilters: updateParsedParams,
  });

  const paginationModel = useMemo(
    () => ({
      page: Number(parsedParams.page) - 1,
      pageSize: Number(parsedParams.pageSize),
    }),
    [parsedParams.page, parsedParams.pageSize]
  );

  const rows =
    users?.results?.map(user => {
      const role = getRoleName(user.groups, groups);
      return {
        ...user,
        ...getQueueAndWorkspacesCounts(
          user,
          queues ?? [],
          isUserAdminLike(role)
        ),
        role,
        name: `${user.firstName} ${user.lastName}`,
        username: user.email || user.username,
      };
    }) ?? [];

  const rowCount = users?.pagination?.total;

  const columns = useUsersColumns({
    onDeleteUserClick: setSelectedUser,
    onToggleUserClick: user =>
      patchUser({
        userId: user.id,
        payload: { isActive: !user.isActive },
      }),
    isActiveToggleDisabled: user =>
      loggedInUserId === user.id || user.role === 'organization_group_admin',
  });

  const handleOnPaginationModelChange = useCallback(
    (newPaginationModel: GridPaginationModel) =>
      updateParsedParams({
        page: `${newPaginationModel.page + 1}`,
        pageSize: pageSizeOptions.find(
          op => op === newPaginationModel.pageSize
        ),
      }),
    [updateParsedParams]
  );

  return (
    <PageLayout
      fullWidth
      renderHeader={params => (
        <UsersHeader
          {...params}
          activeUsersCount={activeUsersCount}
          search={parsedParams.search}
          setSearch={newValue => updateParsedParams({ search: newValue })}
          onExportUsers={() => exportUsers({ deleted: false })}
          isExporting={isExporting}
        />
      )}
    >
      <Stack spacing={3} px={4} py={4} sx={{ flexGrow: 1, minHeight: 0 }}>
        <Filters
          dataCy="users-filters"
          filters={parsedParams}
          setFilters={updateParsedParams}
          filterSettings={filterSettings}
        />
        <DataGridPro
          disableRowSelectionExcludeModel
          columns={columns}
          rows={rows ?? []}
          loading={isLoading || isPreviousData}
          pagination
          rowCount={rowCount ?? 0}
          paginationMode="server"
          onPaginationModelChange={handleOnPaginationModelChange}
          disableColumnMenu
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          disableColumnResize
          disableRowSelectionOnClick
          initialState={{ pinnedColumns: { right: ['actions'] } }}
          onRowClick={({ row }: GridRowParams<OrganizationUser>, event) => {
            if (event.ctrlKey || event.metaKey || event.shiftKey) return;
            push(userDetailsAccessPath(row.id));
          }}
          localeText={{
            noRowsLabel: intl.formatMessage({
              id: 'containers.settings.users.noUsersFound',
            }),
            noResultsOverlayLabel: intl.formatMessage({
              id: 'containers.settings.users.noUsersFound',
            }),
          }}
          slots={{
            columnHeaders: ColumnHeaderPaper,
            loadingOverlay: () => <LinearProgress />,
            row: UserRow,
          }}
          slotProps={{
            basePagination: {
              // @ts-expect-error wrong type
              SelectProps: {
                'data-cy': 'users-pagination-select',
                sx: {
                  [`& .${selectClasses.select}`]: {
                    pt: 0,
                    pb: 0,
                  },
                },
              },
              labelRowsPerPage: intl.formatMessage({
                id: 'containers.allDocuments.pagination.label',
              }),
              sx: {
                display: rowCount === 0 ? 'none' : 'inherit',
                justifyContent: 'flex-end',
              },
            },
          }}
          getCellClassName={({ colDef }) =>
            colDef.type === 'actions' ? 'MuiDataGrid-actionCell' : ''
          }
          sx={{
            // pointer cursor on ALL rows
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-actionCell': {
              cursor: 'default',
            },
            '& .MuiDataGrid-pinnedColumnHeaders--right': {
              // Where is this padding coming from?
              pr: '0 !important',
            },
            ...commonDataGridStyles,
            flexGrow: 1,
            minHeight: 0,
          }}
        />

        <Dialog
          open={selectedUser !== null}
          PaperProps={{
            sx: { width: 480, minHeight: 213, position: 'fixed' },
            elevation: 2,
          }}
          onClose={() => setSelectedUser(null)}
          sx={{ transition: 'smooth' }}
        >
          {selectedUser !== null ? (
            <DeleteUserDialog
              user={selectedUser}
              role={getRoleName(selectedUser.groups, groups)}
              onDelete={() => setSelectedUser(null)}
              onCancel={() => setSelectedUser(null)}
            />
          ) : null}
        </Dialog>
      </Stack>
    </PageLayout>
  );
};
